import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle } from '@artstorefronts/ui';
import { X, Plus, Search, Star, Loader2 } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { GET_CATEGORIES } from '../../graphql/queries';

interface Category {
  id: string;
  name: string;
  slug: string;
  facetId: string;
  isActive: boolean;
  parentId?: string;
  facet?: {
    id: string;
    code: string;
    name: string;
  };
  parent?: Category;
  children?: Category[];
}

interface SelectedCategory {
  categoryId: string;
  category?: Category;
  isPrimary: boolean;
}

interface CategorySelectorProps {
  selectedCategories: SelectedCategory[];
  onCategoriesChange: (categories: SelectedCategory[]) => void;
  maxCategories?: number;
  className?: string;
  isLoading?: boolean;
  isModalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategories,
  onCategoriesChange,
  maxCategories = 6,
  className = '',
  isLoading = false,
  isModalOpen: externalIsModalOpen,
  onModalOpenChange: externalOnModalOpenChange,
}) => {
  const { t } = useTranslation('artwork');
  const { t: tc } = useTranslation('common');
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false);
  const isModalOpen = externalIsModalOpen !== undefined ? externalIsModalOpen : internalIsModalOpen;
  const setIsModalOpen = externalOnModalOpenChange || setInternalIsModalOpen;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFacet, setSelectedFacet] = useState<string>('all');

  const { data: categoriesData, loading } = useQuery(GET_CATEGORIES);

  const categories = useMemo(() => categoriesData?.categories || [], [categoriesData]);

  // Group categories by facet
  const categoriesByFacet = useMemo(() => {
    const grouped: Record<string, Category[]> = {};
    categories.forEach((category: Category) => {
      const facetCode = category.facet?.code || 'unknown';
      if (!grouped[facetCode]) {
        grouped[facetCode] = [];
      }
      grouped[facetCode].push(category);
    });
    return grouped;
  }, [categories]);

  // Group categories by parent and organize hierarchically
  const organizedCategories = useMemo(() => {
    let filtered = categories.filter((cat: Category) => cat.isActive); // Only show active categories

    if (selectedFacet !== 'all') {
      filtered = filtered.filter((cat: Category) => cat.facet?.code === selectedFacet);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();

      // First, find all categories that match the search term
      const matchingCategories = filtered.filter((cat: Category) => cat.name.toLowerCase().includes(term));

      // Get all parent IDs of matching categories
      const parentIds = new Set<string>();
      matchingCategories.forEach((cat: Category) => {
        if (cat.parentId) {
          parentIds.add(cat.parentId);
        }
      });

      // Include categories that match the search term OR are parents of matching children
      // But exclude parent categories that also match the search term themselves
      filtered = filtered.filter((cat: Category) => {
        const isMatchingCategory = cat.name.toLowerCase().includes(term);
        const isParentOfMatchingChild = parentIds.has(cat.id);

        // If it's a parent category that matches the search term, don't include it
        // (it will be shown as part of its own section)
        if (isMatchingCategory && isParentOfMatchingChild) {
          return false;
        }

        return isMatchingCategory || isParentOfMatchingChild;
      });
    }

    // Group by parent (including top-level categories with no parent)
    const groupedByParent: Record<string, Category[]> = {};

    filtered.forEach((category: Category) => {
      const parentId = category.parentId || 'root';
      if (!groupedByParent[parentId]) {
        groupedByParent[parentId] = [];
      }
      groupedByParent[parentId].push(category);
    });

    // Filter out parent categories from the root section since they'll appear in their own sections
    if (groupedByParent['root']) {
      groupedByParent['root'] = groupedByParent['root'].filter((category: Category) => {
        // Check if this category is a parent of any other category
        const isParent = filtered.some((otherCategory: Category) => otherCategory.parentId === category.id);
        return !isParent; // Only keep categories that are NOT parents
      });
    }

    // Convert to array and sort
    const parentSections = Object.entries(groupedByParent).map(([parentId, childCategories]) => {
      const parent = parentId === 'root' ? null : categories.find((cat: Category) => cat.id === parentId);

      return {
        parent,
        categories: childCategories.sort((a, b) => a.name.localeCompare(b.name)),
        facetName: parent?.facet?.name || childCategories[0]?.facet?.name || t('categories.facet-unknown'),
        facetCode: parent?.facet?.code || childCategories[0]?.facet?.code || 'unknown',
      };
    });

    // Filter out empty sections and sections with unknown facets
    const validSections = parentSections.filter((section) => {
      return section.categories.length > 0 && section.facetCode !== 'unknown';
    });

    // Sort by facet first (medium -> style -> subject), then by parent name
    const facetOrder = { medium: 1, style: 2, subject: 3 };

    return validSections.sort((a, b) => {
      const aFacetOrder = facetOrder[a.facetCode as keyof typeof facetOrder] || 999;
      const bFacetOrder = facetOrder[b.facetCode as keyof typeof facetOrder] || 999;

      if (aFacetOrder !== bFacetOrder) {
        return aFacetOrder - bFacetOrder;
      }

      const aName = a.parent?.name || 'Top Level';
      const bName = b.parent?.name || 'Top Level';
      return aName.localeCompare(bName);
    });
  }, [categories, searchTerm, selectedFacet]);

  const handleAddCategory = (category: Category) => {
    if (selectedCategories.length >= maxCategories) return;

    const newSelectedCategory: SelectedCategory = {
      categoryId: category.id,
      category,
      isPrimary: selectedCategories.length === 0, // First category is primary by default
    };

    onCategoriesChange([...selectedCategories, newSelectedCategory]);
  };

  const handleRemoveCategory = (categoryId: string) => {
    const updatedCategories = selectedCategories.filter((cat) => cat.categoryId !== categoryId);

    // If we removed the primary category and there are other categories, make the first one primary
    const removedCategory = selectedCategories.find((cat) => cat.categoryId === categoryId);
    if (removedCategory?.isPrimary && updatedCategories.length > 0) {
      updatedCategories[0].isPrimary = true;
    }

    onCategoriesChange(updatedCategories);
  };

  const handleSetPrimary = (categoryId: string) => {
    const updatedCategories = selectedCategories.map((cat) => ({
      ...cat,
      isPrimary: cat.categoryId === categoryId,
    }));
    onCategoriesChange(updatedCategories);
  };

  const getFacetDisplayName = (facet: string) => {
    // Find the facet name from the categories data
    const facetCategory = categories.find((cat: Category) => cat.facet?.code === facet);
    return facetCategory?.facet?.name || facet;
  };

  const canAddMore = selectedCategories.length < maxCategories;

  return (
    <div className={`space-y-3 ${className}`}>
      <div>
        <label className="text-sm font-medium">{t('categories.title')}</label>
        <p className="text-xs text-gray-500">{t('categories.description', { max: maxCategories })}</p>
      </div>

      {/* Selected Categories Pills */}
      <div className="flex flex-wrap gap-2">
        {selectedCategories.map((selectedCat) => {
          const category =
            selectedCat.category || categories.find((cat: Category) => cat.id === selectedCat.categoryId);
          if (!category) return null;

          return (
            <div
              key={selectedCat.categoryId}
              className="flex items-center gap-2 bg-black text-white px-3 py-1.5 rounded-full text-sm"
            >
              <button
                type="button"
                onClick={() => handleSetPrimary(selectedCat.categoryId)}
                disabled={selectedCat.isPrimary}
                className={`ml-1 ${!selectedCat.isPrimary ? 'hover:text-gray-300 transition-colors' : ''}`}
                title={selectedCat.isPrimary ? t('categories.primary-category') : t('categories.set-as-primary')}
              >
                <Star className={`w-3 h-3 ${selectedCat.isPrimary ? 'fill-current' : ''}`} />
              </button>
              <span>{category.name}</span>
              <button
                type="button"
                onClick={() => handleRemoveCategory(selectedCat.categoryId)}
                className="ml-1 hover:text-gray-300 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          );
        })}

        {/* Add Category Button */}
        {canAddMore && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-3 py-1.5 h-auto rounded-full"
          >
            <Plus className="w-3 h-3" />
            {t('categories.add-category')}
          </Button>
        )}
      </div>

      {/* Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          className="w-full max-w-2xl max-h-[80vh] overflow-hidden pb-0 border-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-left">{t('categories.title')}</DialogTitle>
          </DialogHeader>

          {/* Search and Facet Filter */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t('categories.search-placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-full"
              />
            </div>

            {/* Facet Filter */}
            <div className="flex gap-2 justify-end">
              {t('categories.filter-by')}
              <button
                type="button"
                onClick={() => setSelectedFacet('all')}
                className={`px-3 py-1 rounded-full text-sm ${
                  selectedFacet === 'all' ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tc('all')}
              </button>
              {Object.keys(categoriesByFacet).map((facet) => (
                <button
                  key={facet}
                  type="button"
                  onClick={() => setSelectedFacet(facet)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    selectedFacet === facet ? 'bg-indigo-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getFacetDisplayName(facet)}
                </button>
              ))}
            </div>
          </div>

          {/* Categories List */}
          <div className="overflow-y-auto max-h-96 min-h-96 tiny-scrollbar relative">
            {isLoading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                <div className="text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-600" />
                  <p className="text-sm text-gray-600">{t('categories.selecting-loading')}</p>
                </div>
              </div>
            )}
            {loading ? (
              <div className="text-center py-8 text-gray-500">{t('categories.loading')}</div>
            ) : organizedCategories.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? t('categories.no-search-results') : t('categories.empty-state')}
              </div>
            ) : (
              <div className="space-y-6">
                {organizedCategories.map((section) => (
                  <div key={section.parent?.id || 'root'} className="space-y-3">
                    {/* Section Header */}
                    <div className="flex items-center gap-4 justify-between">
                      <h4 className="font-medium text-gray-900">{section.parent?.name}</h4>
                      <span className="text-xs text-indigo-500 mt-0.5 mr-2">{section.facetName}</span>
                    </div>

                    {/* Category Pills */}
                    <div className="flex flex-wrap gap-2">
                      {/* Parent category pill (if it exists and matches search) */}
                      {section.parent &&
                        (() => {
                          const isSelected = selectedCategories.some(
                            (selected) => selected.categoryId === section.parent!.id
                          );
                          const parentMatchesSearch = searchTerm
                            ? section.parent.name.toLowerCase().includes(searchTerm.toLowerCase())
                            : true;

                          if (!parentMatchesSearch) return null;

                          return (
                            <div
                              key={section.parent.id}
                              className={`px-4 py-2 rounded-full text-sm cursor-pointer transition-colors ${
                                isSelected ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                              onClick={() =>
                                isSelected
                                  ? handleRemoveCategory(section.parent!.id)
                                  : handleAddCategory(section.parent!)
                              }
                            >
                              {section.parent.name}
                            </div>
                          );
                        })()}

                      {/* Child category pills */}
                      {section.categories.map((category: Category) => {
                        const isSelected = selectedCategories.some((selected) => selected.categoryId === category.id);

                        return (
                          <div
                            key={category.id}
                            className={`px-4 py-2 rounded-full text-sm cursor-pointer transition-colors ${
                              isSelected ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            onClick={() =>
                              isSelected ? handleRemoveCategory(category.id) : handleAddCategory(category)
                            }
                          >
                            {category.name}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Categories Row at Bottom of Modal */}
          {selectedCategories.length > 0 && (
            <div className="border-t py-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-700">{t('categories.selected-label')}</span>
                <span className="text-xs text-gray-500">
                  {selectedCategories.length}/{maxCategories}
                </span>
              </div>
              <div className="hidden md:block">
                <div className="flex gap-2 flex-wrap">
                  {selectedCategories.map((selectedCat) => {
                    const category =
                      selectedCat.category || categories.find((cat: Category) => cat.id === selectedCat.categoryId);
                    if (!category) return null;

                    return (
                      <div
                        key={selectedCat.categoryId}
                        className="flex items-center bg-black text-white px-3 py-1.5 rounded-full text-xs flex-shrink-0"
                      >
                        <span>{category.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCategory(selectedCat.categoryId)}
                          className="ml-1 hover:text-gray-300 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
